import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Eye, Copy, Trash2, X } from 'lucide-react'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../../components/ModalConfirmation'
import { RowActionsMenu } from '../../../../../components/StandardPage'
import { ApprovalApi } from '../../../ProductComposition/ApprovationModal/api'
import {
  ProductApprovalListItem,
  ProductApprovalStatus,
  ProductApprovalType,
} from '../../../ProductComposition/ApprovationModal/types'
import {
  EmptyState,
  InlineFormRow,
  Input,
  Label,
  NewApprovalForm,
  PrimaryButton,
  SimpleModalBody,
  SimpleModalBox,
  SimpleModalCloseBtn,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalOverlay,
  SimpleModalTitle,
  StatusColorDot,
  StatusDropdownIcon,
  StatusDropdownItem,
  StatusDropdownMenu,
  StatusInputContainer,
  StatusTag,
  Table,
  TableWrapper,
  Td,
  Th,
  ToolbarRow,
  Tr,
} from '../../../ProductComposition/ApprovationModal/styles'
import ApprovationModal from '../../../ProductComposition/ApprovationModal'


interface Props {
  productId: number
  productCode?: string
}

interface ShowroomItem {
  id: number
  name: string
  productCode: string | null
  status?: boolean | null
  createdAt: string
  price?: number | null
  CoverGeneralFile?: { id: number; fileUrl: string } | null
}

const statusConfig: Record<
  ProductApprovalStatus,
  { label: string; bg: string; fg: string; dot: string }
> = {
  IN_PROGRESS: { label: 'Em andamento', bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
  APPROVED: { label: 'Aprovado', bg: '#d1fae5', fg: '#065f46', dot: '#10b981' },
  REJECTED: { label: 'Reprovado', bg: '#fee2e2', fg: '#991b1b', dot: '#ef4444' },
}

const statusOptions: {
  value: ProductApprovalStatus
  label: string
  bg: string
  fg: string
  dot: string
}[] = [
  { value: 'IN_PROGRESS', label: 'Em andamento', bg: '#fef3c7', fg: '#92400e', dot: '#f59e0b' },
  { value: 'APPROVED', label: 'Aprovado', bg: '#d1fae5', fg: '#065f46', dot: '#10b981' },
  { value: 'REJECTED', label: 'Reprovado', bg: '#fee2e2', fg: '#991b1b', dot: '#ef4444' },
]

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR')
}

const isPastDeadline = (iso: string | null | undefined) => {
  if (!iso) return false
  const deadline = new Date(iso)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return deadline < today
}

const StatusTagSelect: React.FC<{
  status: ProductApprovalStatus
  onChange: (newStatus: ProductApprovalStatus) => void
}> = ({ status, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const current = statusConfig[status] || statusConfig.IN_PROGRESS

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setCoords({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(170, rect.width),
      })
    }
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleScroll = () => setIsOpen(false)

    window.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen])

  return (
    <>
      <StatusInputContainer ref={containerRef} onClick={handleToggle}>
        <StatusTag $bg={current.bg} $fg={current.fg}>
          {current.label}
        </StatusTag>
        <StatusDropdownIcon />
      </StatusInputContainer>

      {isOpen && coords && (
        <StatusDropdownMenu
          ref={dropdownRef}
          style={{ top: coords.top, left: coords.left, width: coords.width }}
          onClick={(e) => e.stopPropagation()}
        >
          {statusOptions.map((opt) => (
            <StatusDropdownItem
              key={opt.value}
              type='button'
              $isSelected={opt.value === status}
              onClick={(e) => {
                e.stopPropagation()
                setIsOpen(false)
                if (opt.value !== status) {
                  onChange(opt.value)
                }
              }}
            >
              <StatusColorDot $color={opt.dot} />
              {opt.label}
            </StatusDropdownItem>
          ))}
        </StatusDropdownMenu>
      )}
    </>
  )
}

const ApprovationsTable: React.FC<Props> = ({ productId, productCode }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const approvalIdParam = searchParams.get('approvalId')
  const { setFeedback } = useContext(FeedbackContext)
  const [showrooms, setShowrooms] = useState<ShowroomItem[]>([])
  const [approvals, setApprovals] = useState<ProductApprovalListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<ProductApprovalType>('PILOT')
  const [creatingBusy, setCreatingBusy] = useState(false)
  const [openId, setOpenId] = useState<number | null>(() => {
    if (approvalIdParam) {
      const parsed = Number(approvalIdParam)
      return !Number.isNaN(parsed) && parsed > 0 ? parsed : null
    }
    return null
  })

  useEffect(() => {
    if (approvalIdParam) {
      const parsed = Number(approvalIdParam)
      if (!Number.isNaN(parsed) && parsed > 0) {
        setOpenId(parsed)
      }
    }
  }, [approvalIdParam])
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null)
  const [deleteCandidate, setDeleteCandidate] = useState<{
    id: number
    name: string
    isShowroom?: boolean
  } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingBusy, setDeletingBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    try {
      const [showroomsData, approvalsData] = await Promise.allSettled([
        ApprovalApi.listShowrooms(productId),
        ApprovalApi.list(productId),
      ])

      if (showroomsData.status === 'fulfilled') {
        setShowrooms(Array.isArray(showroomsData.value) ? showroomsData.value : [])
      } else {
        setShowrooms([])
      }

      if (approvalsData.status === 'fulfilled') {
        setApprovals(Array.isArray(approvalsData.value) ? approvalsData.value : [])
      } else {
        setApprovals([])
      }
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao carregar aprovações.',
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }, [productId, setFeedback])

  useEffect(() => {
    reload()
  }, [reload])

  const suggestedBaseCode = useMemo(() => {
    if (productCode && productCode.trim()) {
      return productCode.endsWith('.') ? productCode : `${productCode}.`
    }
    return ''
  }, [productCode])

  const startCreate = () => {
    setNewType('PILOT')
    setNewName(`Piloto ${approvals.length + 1}`)
    setNewCode(suggestedBaseCode)
    setCreating(true)
  }

  const handleCreate = async () => {
    if (newType === 'SHOWROOM') {
      if (!newCode.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Por favor, informe o código do produto.',
          statusOk: false,
        })
        return
      }

      if (!newName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Por favor, informe o nome do produto.',
          statusOk: false,
        })
        return
      }

      setCreatingBusy(true)
      try {
        const created = await ApprovalApi.createShowroom(productId, {
          productCode: newCode.trim(),
          name: newName.trim(),
        })
        setCreating(false)
        setFeedback({
          isOpen: true,
          message: `Mostruário "${created.name}" criado com sucesso!`,
          statusOk: true,
        })
        navigate(`/panel/composition-product/edit/${created.id}?parentId=${productId}`)
      } catch (err: any) {
        setFeedback({
          isOpen: true,
          message: err?.response?.data?.message ?? 'Erro ao criar mostruário.',
          statusOk: false,
        })
      } finally {
        setCreatingBusy(false)
      }
    } else {
      if (!newName.trim()) {
        setFeedback({
          isOpen: true,
          message: 'Por favor, informe o nome da aprovação.',
          statusOk: false,
        })
        return
      }

      setCreatingBusy(true)
      try {
        const created = await ApprovalApi.create({
          productId,
          name: newName.trim(),
          type: 'PILOT',
        })
        setCreating(false)
        setFeedback({
          isOpen: true,
          message: `Aprovação "${created.name}" criada com sucesso!`,
          statusOk: true,
        })
        await reload()
        setOpenId(created.id)
      } catch (err: any) {
        setFeedback({
          isOpen: true,
          message: err?.response?.data?.message ?? 'Erro ao criar aprovação.',
          statusOk: false,
        })
      } finally {
        setCreatingBusy(false)
      }
    }
  }

  const handleOpenProduct = (id: number) => {
    navigate(`/panel/composition-product/edit/${id}?parentId=${productId}`)
  }

  const handleOpenProductInNewTab = (id: number) => {
    window.open(`/panel/composition-product/edit/${id}?parentId=${productId}`, '_blank')
  }

  const handleStatusChange = async (
    row: ProductApprovalListItem,
    newStatus: ProductApprovalStatus,
  ) => {
    const previousStatus = row.status
    if (newStatus === previousStatus) return

    setApprovals((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)),
    )

    try {
      await ApprovalApi.update(row.id, { status: newStatus })
      setFeedback({
        isOpen: true,
        message: `Status atualizado para "${statusConfig[newStatus]?.label || newStatus}" com sucesso!`,
        statusOk: true,
      })
    } catch (err: any) {
      setApprovals((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: previousStatus } : r)),
      )
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao atualizar status.',
        statusOk: false,
      })
    }
  }

  const handleDuplicate = async (row: ProductApprovalListItem, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setDuplicatingId(row.id)
    try {
      const duplicated = await ApprovalApi.duplicate(row.id)
      setFeedback({
        isOpen: true,
        message: `Aprovação "${duplicated.name}" duplicada com sucesso!`,
        statusOk: true,
      })
      await reload()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao duplicar aprovação.',
        statusOk: false,
      })
    } finally {
      setDuplicatingId(null)
    }
  }

  const [copyModalShowroom, setCopyModalShowroom] = useState<ShowroomItem | null>(null)
  const [copyModalCode, setCopyModalCode] = useState('')
  const [copyModalName, setCopyModalName] = useState('')
  const [copyModalBusy, setCopyModalBusy] = useState(false)

  const handleOpenCopyModal = (row: ShowroomItem, e?: React.MouseEvent) => {
    e?.stopPropagation()
    let base = productCode ? (productCode.endsWith('.') ? productCode : `${productCode}.`) : ''
    if (!base && row.productCode) {
      const parts = row.productCode.split('.')
      base = parts[0] ? `${parts[0]}.` : ''
    }
    setCopyModalCode(base)
    setCopyModalName(row.name ? `${row.name} (Cópia)` : 'Mostruário')
    setCopyModalShowroom(row)
  }


  const handleConfirmCopyShowroom = async () => {
    if (!copyModalShowroom || !copyModalCode.trim() || !copyModalName.trim()) return
    setCopyModalBusy(true)
    try {
      const duplicated = await ApprovalApi.duplicateShowroom(productId, copyModalShowroom.id, {
        productCode: copyModalCode.trim(),
        name: copyModalName.trim(),
      })
      setFeedback({
        isOpen: true,
        message: `Mostruário "${duplicated.name}" copiado com sucesso!`,
        statusOk: true,
      })
      setCopyModalShowroom(null)
      await reload()
      handleOpenProduct(duplicated.id)
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao copiar mostruário.',
        statusOk: false,
      })
    } finally {
      setCopyModalBusy(false)
    }
  }

  const handleDeleteClick = (
    item: { id: number; name: string; isShowroom?: boolean },
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation()
    setDeleteCandidate(item)
    setShowDeleteConfirm(true)
  }


  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return
    setDeletingBusy(true)
    try {
      if (deleteCandidate.isShowroom) {
        await ApprovalApi.deleteProduct(deleteCandidate.id)
      } else {
        await ApprovalApi.remove(deleteCandidate.id)
      }
      setFeedback({
        isOpen: true,
        message: 'Registro excluído com sucesso!',
        statusOk: true,
      })
      await reload()
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao excluir registro.',
        statusOk: false,
      })
    } finally {
      setDeletingBusy(false)
      setShowDeleteConfirm(false)
      setDeleteCandidate(null)
    }
  }

  return (
    <div>
      <ToolbarRow>
        {!creating && (
          <PrimaryButton type='button' onClick={startCreate}>
            <Plus size={14} /> Nova aprovação
          </PrimaryButton>
        )}
      </ToolbarRow>

      {creating && (
        <NewApprovalForm>
          <InlineFormRow>
            {newType === 'SHOWROOM' && (
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
                <Label>Código do produto</Label>
                <Input
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder='Ex.: 227800.1'
                  disabled={creatingBusy}
                  autoFocus
                />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
              <Label>{newType === 'SHOWROOM' ? 'Nome do produto' : 'Nome'}</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={newType === 'SHOWROOM' ? 'Ex.: Mostruário 1' : 'Ex.: Piloto 1'}
                disabled={creatingBusy}
                autoFocus={newType === 'PILOT'}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
              <Label>Tipo</Label>
              <select
                value={newType}
                onChange={(e) => {
                  const val = e.target.value as ProductApprovalType
                  setNewType(val)
                  if (val === 'SHOWROOM') {
                    setNewName('Mostruário')
                    setNewCode(suggestedBaseCode)
                  } else {
                    setNewName(`Piloto ${approvals.length + 1}`)
                  }
                }}
                disabled={creatingBusy}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 13,
                  background: '#fff',
                }}
              >
                <option value='PILOT'>Piloto</option>
                <option value='SHOWROOM'>Mostruário</option>
              </select>
            </div>
            <PrimaryButton
              type='button'
              onClick={handleCreate}
              disabled={
                creatingBusy ||
                !newName.trim() ||
                (newType === 'SHOWROOM' && !newCode.trim())
              }
            >
              {creatingBusy ? 'Criando...' : 'Criar e abrir'}
            </PrimaryButton>
            <button
              type='button'
              onClick={() => setCreating(false)}
              disabled={creatingBusy}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                padding: '8px 10px',
              }}
            >
              Cancelar
            </button>
          </InlineFormRow>
        </NewApprovalForm>
      )}

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>Nome</Th>
              <Th style={{ minWidth: 175, width: 175 }}>Status</Th>
              <Th>Fornecedor</Th>
              <Th>Criado por</Th>
              <Th>Aprovador</Th>
              <Th>Data da prova</Th>
              <Th>Entrega Prevista</Th>
              <Th>Valid./Tam.</Th>
              <Th style={{ textAlign: 'center', width: 120 }}>Ações</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <Td colSpan={9}>
                  <EmptyState>Carregando aprovações...</EmptyState>
                </Td>
              </tr>
            )}
            {!loading && showrooms.length === 0 && approvals.length === 0 && (
              <tr>
                <Td colSpan={9}>
                  <EmptyState>
                    Nenhuma aprovação cadastrada ainda. Clique em &quot;Nova aprovação&quot; para começar.
                  </EmptyState>
                </Td>
              </tr>
            )}
            {!loading &&
              approvals.map((row) => (
                <Tr key={`approval-${row.id}`} $clickable onClick={() => setOpenId(row.id)}>
                  <Td style={{ fontWeight: 700 }}>{row.name}</Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <StatusTagSelect
                      status={row.status}
                      onChange={(newStatus) => handleStatusChange(row, newStatus)}
                    />
                  </Td>
                  <Td>{row.SupplierCompany?.companyName ?? '—'}</Td>
                  <Td>{row.CreatedBy?.name ?? '—'}</Td>
                  <Td>{row.Approver?.name ?? '—'}</Td>
                  <Td>{formatDate(row.startDate)}</Td>
                  <Td
                    style={{
                      color:
                        row.status !== 'APPROVED' && isPastDeadline(row.expectedDeliveryDate)
                          ? '#dc2626'
                          : undefined,
                      fontWeight:
                        row.status !== 'APPROVED' && isPastDeadline(row.expectedDeliveryDate)
                          ? 600
                          : undefined,
                    }}
                  >
                    {formatDate(row.expectedDeliveryDate)}
                  </Td>
                  <Td>{(row.sizes ?? []).join(', ') || '—'}</Td>
                  <Td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', width: 70 }}>
                    <RowActionsMenu
                      actions={[
                        {
                          label: 'Visualizar',
                          icon: <Eye size={16} />,
                          onClick: () => setOpenId(row.id),
                        },
                        {
                          label: 'Duplicar',
                          icon: <Copy size={16} />,
                          onClick: () => handleDuplicate(row),
                          disabled: duplicatingId === row.id,
                        },
                        {
                          label: 'Excluir',
                          icon: <Trash2 size={16} color='#dc2626' />,
                          onClick: () => handleDeleteClick({ id: row.id, name: row.name, isShowroom: false }),
                        },
                      ]}
                    />
                  </Td>
                </Tr>
              ))}
            {!loading &&
              showrooms.map((row) => (
                <Tr key={`showroom-${row.id}`} $clickable onClick={() => handleOpenProductInNewTab(row.id)}>
                  <Td style={{ fontWeight: 700, color: '#0284c7' }}>
                    {row.productCode ? `${row.productCode} - ` : ''}{row.name}
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                      }}
                    >
                      Mostruário
                    </span>
                  </Td>
                  <Td>—</Td>
                  <Td>—</Td>
                  <Td>—</Td>
                  <Td>{formatDate(row.createdAt)}</Td>
                  <Td>—</Td>
                  <Td>—</Td>
                  <Td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', width: 70 }}>
                    <RowActionsMenu
                      actions={[
                        {
                          label: 'Visualizar',
                          icon: <Eye size={16} />,
                          onClick: () => handleOpenProductInNewTab(row.id),
                        },
                        {
                          label: 'Copiar',
                          icon: <Copy size={16} />,
                          onClick: () => handleOpenCopyModal(row),
                        },
                        {
                          label: 'Excluir',
                          icon: <Trash2 size={16} color='#dc2626' />,
                          onClick: () => handleDeleteClick({ id: row.id, name: row.name, isShowroom: true }),
                        },
                      ]}
                    />
                  </Td>
                </Tr>
              ))}

          </tbody>
        </Table>
      </TableWrapper>

      {openId && (
        <ApprovationModal
          approvalId={openId}
          onClose={() => {
            setOpenId(null)
            reload()
          }}
          onChanged={reload}
        />
      )}

      {copyModalShowroom && (
        <SimpleModalOverlay>
          <SimpleModalBox onClick={(e) => e.stopPropagation()}>
            <SimpleModalHeader>
              <SimpleModalTitle>Copiar Mostruário</SimpleModalTitle>
              <SimpleModalCloseBtn
                type='button'
                onClick={() => !copyModalBusy && setCopyModalShowroom(null)}
                disabled={copyModalBusy}
              >
                <X size={18} />
              </SimpleModalCloseBtn>
            </SimpleModalHeader>
            <SimpleModalBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Código do produto</Label>
                <Input
                  value={copyModalCode}
                  onChange={(e) => setCopyModalCode(e.target.value)}
                  placeholder='Ex.: 227800.1'
                  disabled={copyModalBusy}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Label>Nome do produto</Label>
                <Input
                  value={copyModalName}
                  onChange={(e) => setCopyModalName(e.target.value)}
                  placeholder='Ex.: Mostruário 1'
                  disabled={copyModalBusy}
                />
              </div>
            </SimpleModalBody>
            <SimpleModalFooter>
              <button
                type='button'
                onClick={() => setCopyModalShowroom(null)}
                disabled={copyModalBusy}
                style={{
                  background: 'none',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  color: '#4b5563',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '8px 14px',
                }}
              >
                Cancelar
              </button>
              <PrimaryButton
                type='button'
                onClick={handleConfirmCopyShowroom}
                disabled={copyModalBusy || !copyModalCode.trim() || !copyModalName.trim()}
              >
                {copyModalBusy ? 'Copiando...' : 'Criar e abrir'}
              </PrimaryButton>
            </SimpleModalFooter>
          </SimpleModalBox>
        </SimpleModalOverlay>
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={deleteCandidate?.isShowroom ? 'Excluir Mostruário' : 'Excluir Aprovação'}
        message={
          deleteCandidate
            ? deleteCandidate.isShowroom
              ? `Tem certeza que deseja excluir "${deleteCandidate.name}"? Esta ação removerá o produto mostruário e não poderá ser desfeita.`
              : `Tem certeza que deseja excluir a aprovação "${deleteCandidate.name}"? Todas as informações e subcards associados serão removidos permanentemente.`
            : 'Tem certeza que deseja excluir este registro?'
        }
        actions={[
          {
            label: 'Não, voltar',
            onClick: () => {
              if (!deletingBusy) {
                setShowDeleteConfirm(false)
                setDeleteCandidate(null)
              }
            },
            variant: 'outline',
          },
          {
            label: deletingBusy ? 'Excluindo...' : 'Sim, excluir',
            onClick: handleConfirmDelete,
            variant: 'danger',
          },
        ]}
      />
    </div>
  )
}


export default ApprovationsTable
